import React from 'react';
import { useTranslation } from 'react-i18next';
import { listAppointmentTypes } from '../../calendar/services/calendarService';
import type { AppointmentType } from '../../calendar/types/Calendar';
import { listConsultationTemplates } from '../../consultations/services/consultationService';
import type { ConsultationTemplate } from '../../consultations/types/Consultation';
import { listTaskCategories } from '../../tasks/services/taskService';
import type { TaskCategoryItem } from '../../tasks/types/Task';
import type { ReminderEngineSubjectParameter } from '../types/ReminderEngine';
import type { CriterionFormState } from '../utils/definitionEditorForm';
import formStyles from './DefinitionEditorForm.module.css';

type RuleCriteriaEditorProps = {
  subjectType: string;
  parameters: ReminderEngineSubjectParameter[];
  criteria: CriterionFormState[];
  busy: boolean;
  onChange: (criteria: CriterionFormState[]) => void;
};

const FILTERABLE_KEYS_WITH_PICKERS = new Set([
  'appointmentTypeId',
  'consultationTemplateId',
  'categoryId',
  'categoryCode',
  'priority',
]);

export const RuleCriteriaEditor: React.FC<RuleCriteriaEditorProps> = ({
  subjectType,
  parameters,
  criteria,
  busy,
  onChange,
}) => {
  const { t } = useTranslation('reminderEngine');
  const filterableParameters = React.useMemo(
    () => parameters.filter((parameter) => parameter.filterable),
    [parameters],
  );
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [consultationTemplates, setConsultationTemplates] = React.useState<ConsultationTemplate[]>([]);
  const [taskCategories, setTaskCategories] = React.useState<TaskCategoryItem[]>([]);

  React.useEffect(() => {
    let cancelled = false;

    const loadLookups = async () => {
      if (subjectType === 'appointment') {
        const [types, templates] = await Promise.all([
          listAppointmentTypes(),
          listConsultationTemplates(),
        ]);
        if (!cancelled) {
          setAppointmentTypes(types);
          setConsultationTemplates(templates);
          setTaskCategories([]);
        }
        return;
      }

      if (subjectType === 'task') {
        const categories = await listTaskCategories();
        if (!cancelled) {
          setTaskCategories(categories);
          setAppointmentTypes([]);
          setConsultationTemplates([]);
        }
        return;
      }

      if (!cancelled) {
        setAppointmentTypes([]);
        setConsultationTemplates([]);
        setTaskCategories([]);
      }
    };

    void loadLookups().catch(() => {
      if (!cancelled) {
        setAppointmentTypes([]);
        setConsultationTemplates([]);
        setTaskCategories([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [subjectType]);

  const updateCriterion = (index: number, patch: Partial<CriterionFormState>) => {
    onChange(criteria.map((criterion, criterionIndex) => (
      criterionIndex === index ? { ...criterion, ...patch } : criterion
    )));
  };

  const addCriterion = () => {
    onChange([...criteria, { key: '', operator: 'equals', value: '' }]);
  };

  const removeCriterion = (index: number) => {
    onChange(criteria.filter((_, criterionIndex) => criterionIndex !== index));
  };

  const renderValueInput = (criterion: CriterionFormState, index: number) => {
    if (criterion.key === 'appointmentTypeId') {
      return (
        <select
          className="text-input"
          value={criterion.value}
          onChange={(event) => updateCriterion(index, { value: event.target.value })}
          disabled={busy}
          aria-label={t('criterionValue')}
        >
          <option value="">{t('selectValue')}</option>
          {appointmentTypes.map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </select>
      );
    }

    if (criterion.key === 'consultationTemplateId') {
      return (
        <select
          className="text-input"
          value={criterion.value}
          onChange={(event) => updateCriterion(index, { value: event.target.value })}
          disabled={busy}
          aria-label={t('criterionValue')}
        >
          <option value="">{t('selectValue')}</option>
          {consultationTemplates.map((template) => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </select>
      );
    }

    if (criterion.key === 'categoryId') {
      return (
        <select
          className="text-input"
          value={criterion.value}
          onChange={(event) => updateCriterion(index, { value: event.target.value })}
          disabled={busy}
          aria-label={t('criterionValue')}
        >
          <option value="">{t('selectValue')}</option>
          {taskCategories.map((category) => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      );
    }

    if (criterion.key === 'categoryCode') {
      return (
        <select
          className="text-input"
          value={criterion.value}
          onChange={(event) => updateCriterion(index, { value: event.target.value })}
          disabled={busy}
          aria-label={t('criterionValue')}
        >
          <option value="">{t('selectValue')}</option>
          {taskCategories.map((category) => (
            <option key={category.id} value={category.code || category.id}>{category.name} ({category.code || category.id})</option>
          ))}
        </select>
      );
    }

    if (criterion.key === 'priority') {
      return (
        <select
          className="text-input"
          value={criterion.value}
          onChange={(event) => updateCriterion(index, { value: event.target.value })}
          disabled={busy}
          aria-label={t('criterionValue')}
        >
          <option value="">{t('selectValue')}</option>
          {['Low', 'Medium', 'High', 'Urgent'].map((priority) => (
            <option key={priority} value={priority}>{priority}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        className="text-input"
        value={criterion.value}
        onChange={(event) => updateCriterion(index, { value: event.target.value })}
        disabled={busy}
        placeholder={criterion.operator === 'in' || criterion.operator === 'notIn' ? t('commaSeparatedValues') : undefined}
        aria-label={t('criterionValue')}
      />
    );
  };

  return (
    <div className={formStyles.criteriaSection}>
      <div className={formStyles.criteriaHeader}>
        <div>
          <strong>{t('ruleCriteria')}</strong>
          <p className={formStyles.criteriaHint}>{t('ruleCriteriaHint')}</p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={addCriterion} disabled={busy}>
          {t('addCriterion')}
        </button>
      </div>

      {criteria.length === 0 ? (
        <p className={formStyles.criteriaEmpty}>{t('noCriteriaAppliesToAll')}</p>
      ) : null}

      {criteria.map((criterion, index) => (
        <div key={`criterion-${index}`} className={formStyles.criterionRow}>
          <select
            className="text-input"
            value={criterion.key}
            onChange={(event) => updateCriterion(index, { key: event.target.value, value: '' })}
            disabled={busy}
            aria-label={t('criterionField')}
          >
            <option value="">{t('selectCriterionField')}</option>
            {filterableParameters.map((parameter) => (
              <option key={parameter.key} value={parameter.key}>{parameter.label}</option>
            ))}
          </select>
          <select
            className="text-input"
            value={criterion.operator}
            onChange={(event) => updateCriterion(index, { operator: event.target.value as CriterionFormState['operator'] })}
            disabled={busy}
            aria-label={t('criterionOperator')}
          >
            <option value="equals">{t('operatorEquals')}</option>
            <option value="notEquals">{t('operatorNotEquals')}</option>
            <option value="in">{t('operatorIn')}</option>
            <option value="notIn">{t('operatorNotIn')}</option>
            <option value="contains">{t('operatorContains')}</option>
            <option value="greaterThan">{t('operatorGreaterThan')}</option>
            <option value="lessThan">{t('operatorLessThan')}</option>
          </select>
          {FILTERABLE_KEYS_WITH_PICKERS.has(criterion.key)
            ? renderValueInput(criterion, index)
            : (
              <input
                className="text-input"
                value={criterion.value}
                onChange={(event) => updateCriterion(index, { value: event.target.value })}
                disabled={busy}
                placeholder={criterion.operator === 'in' || criterion.operator === 'notIn' ? t('commaSeparatedValues') : undefined}
                aria-label={t('criterionValue')}
              />
            )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => removeCriterion(index)} disabled={busy}>
            {t('removeCriterion')}
          </button>
        </div>
      ))}
    </div>
  );
};
